import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  type ReactElement,
  type ReactNode,
} from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formControlClass } from "../FormControls";

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = 500,
  busy = false,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: number;
  /**
   * When true (e.g. a save is in flight), ESC, backdrop click and the X button
   * do not close the modal — prevents losing entered data mid-submit. Optional;
   * default false keeps the existing behavior for every current caller.
   */
  busy?: boolean;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, busy]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-[2px] animate-fadeIn"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        // --app-h suit la barre d'URL mobile (90vh débordait sous le clavier).
        className="w-[90%] max-h-[calc(var(--app-h)*0.92)] overflow-y-auto bg-card border border-border2 rounded-lg shadow-floating scroll-thin"
        style={{ maxWidth }}
      >
        <div className="flex items-center justify-between px-4 sm:px-5 py-4 sm:py-5 border-b border-border">
          <h2 className="font-display text-base font-bold text-text">{title}</h2>
          <button
            onClick={onClose}
            disabled={busy}
            className="flex items-center justify-center h-9 w-9 -me-1.5 rounded-sm text-text2 hover:bg-bg4 hover:text-text transition-colors disabled:opacity-40 disabled:pointer-events-none"
            aria-label={t("common.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4 sm:p-6">{children}</div>
        {footer && (
          <div className="flex flex-wrap justify-end gap-2.5 px-4 sm:px-6 py-4 border-t border-border">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/** Éléments qu'un <label htmlFor> peut légalement désigner. */
const LABELABLE = new Set(["input", "select", "textarea"]);

/**
 * Décide si le libellé d'un `Field` peut désigner son enfant, et quels attributs
 * d'accessibilité lui greffer. Extrait du composant pour être testable : ce
 * projet n'a pas de rendu de composants dans sa suite de tests.
 */
export function fieldAccessibility(opts: {
  /** `child.type` : une chaîne pour un élément natif, une fonction sinon. */
  childType: unknown;
  /** Identifiant déjà porté par l'enfant — il a la priorité. */
  childId?: string;
  generatedId: string;
  required?: boolean;
  error?: string;
}): { native: boolean; controlId: string; props: Record<string, unknown> } {
  const { childType, childId, generatedId, required, error } = opts;
  const native = typeof childType === "string" && LABELABLE.has(childType);
  const controlId = childId ?? generatedId;
  return {
    native,
    controlId,
    props: {
      id: controlId,
      "aria-required": required || undefined,
      "aria-invalid": error ? true : undefined,
      "aria-describedby": error ? `${generatedId}-error` : undefined,
    },
  };
}

export function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
}) {
  const generatedId = useId();
  const errorId = `${generatedId}-error`;

  // Le libellé était rendu sans `htmlFor` et les champs sont ses FRÈRES, pas ses
  // descendants : cliquer dessus ne faisait rien et un lecteur d'écran
  // n'annonçait aucun nom. L'astérisque « requis » était purement décoratif.
  // On greffe donc l'identité et les états sur l'enfant quand c'est un contrôle
  // natif ; les champs composites (combobox, sélection multiple…) portent leur
  // propre accessibilité et sont laissés intacts.
  const child = isValidElement(children) ? children : null;
  const { native, controlId, props } = fieldAccessibility({
    childType: child?.type,
    childId: (child?.props as { id?: string } | undefined)?.id,
    generatedId,
    required,
    error,
  });

  const control =
    native && child ? cloneElement(child as ReactElement<Record<string, unknown>>, props) : children;

  return (
    <div className="mb-4">
      <label
        htmlFor={native ? controlId : undefined}
        className="block text-[11px] font-semibold uppercase tracking-wide text-text2 mb-1.5"
      >
        {label}{" "}
        {required && (
          <span className="text-danger" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {control}
      {error && (
        <p id={errorId} className="mt-1 text-[11px] text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export const fieldInputClass = formControlClass;
